import {createDrawerNavigator, DrawerContentComponentProps, DrawerContentScrollView} from '@react-navigation/drawer';
import {NavigationState, PartialState, Route, DrawerActions} from '@react-navigation/native';
import * as React from 'react';
import {ComponentType} from 'react';
import {Platform, StyleSheet, TouchableOpacity, View, ViewStyle} from 'react-native';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../redux';
import {SafeAreaView} from 'react-native-safe-area-context';

import {Logo, LogoSVG} from '../../assets/images';
import {changeThemeProperty} from '../../redux/actions/runtime/theme';
import {holderProfileSelector, profileTokenSelector} from '../../redux/selectors/profile';
import {themeSelector} from '../../redux/selectors/runtime/theme';
import {UnreadNotificationsCounter} from '../components/counter';
import {Image} from '../components/image';
import {VisImpStatus} from '../models/visuallyImpairedSettings';
import {useTheme} from '../theme';
import {Theme} from '../theme/interface';
import {BottomStackNavigator} from './bottomTabs';
import {Screens} from './model';
import {openTabRoot} from './openTabRoot';
import {Text} from '../components/text';
import {images} from '../../assets/icons';
import {useLocality} from '../hooks/useLocality';
import {HolderAvatar} from '../components/holderAvatar';
import {initialLoading} from './navigation';
import {IconProps} from '../components/svg/model';
import {useAlign} from '../theme/helpers';
import {Settings} from '../constants/config';

const contentHorizontalOffset = 24;
const counterRight = -22;
const logoSizes = {width: 106, height: 86};
const iconSize = 18;

const Drawer = createDrawerNavigator();

const IconBlock: React.FC<IconProps & {iconComponent: ComponentType<IconProps>}> = (props) => {
  const style: ViewStyle = {
    height: iconSize,
    width: iconSize,
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
  };
  const align = useAlign();
  const {iconComponent: Icon, color, strokeWidth} = props;
  return (
    <View style={style}>
      <Icon
        color={color}
        width={iconSize}
        height={iconSize}
        strokeWidth={strokeWidth !== undefined ? align(strokeWidth) : undefined}
      />
    </View>
  );
};

const LogoBlock: React.FC = () => {
  const align = useAlign();
  const imageSource = Platform.OS === 'web' ? LogoSVG : Logo;
  return <Image source={imageSource as any} style={{width: align(logoSizes.width), height: align(logoSizes.height)}} />;
};

const mobileMenuItems: {
  name: string | ((textStyle: any) => React.ReactNode);
  screen: Screens | [Screens, object];
  thin?: boolean;
}[] = [
  {
    name: 'Главная',
    screen: Screens.Main,
  },
  {
    name: 'Возможности карты',
    screen: Screens.CardServicesMenu,
  },
  // {
  //   name: 'Меры социальной поддержки',
  //   screen: Screens.SocialSupportMeasuresMenu,
  // },
  {
    name: 'Опросы и голосования',
    screen: Screens.PollsMenu,
  },
  {
    name: 'Новости',
    screen: Screens.News,
  },
  {
    name: 'Служба поддержки',
    screen: [Screens.SupportServiceTab, {screen: Screens.SupportService}],
  },
  {
    name: (style) => (
      <UnreadNotificationsCounter counterStyle={{right: counterRight}}>
        <Text style={style}>Уведомления</Text>
      </UnreadNotificationsCounter>
    ),
    screen: [Screens.NotificationsMenu, {screen: Screens.Notifications}],
  },
];

type RouteWithOptionalState = Route<string> & {
  state?: NavigationState | PartialState<NavigationState>;
};

const getActiveRoutes = (rootState: NavigationState | undefined): RouteWithOptionalState[] => {
  if (!rootState || !rootState.routes || rootState.routes.length === 0) {
    return [];
  }

  const getRoutes = (route: RouteWithOptionalState): RouteWithOptionalState[] => {
    if (!route) {
      return [];
    }
    const {state} = route;
    if (!state || !state.routes || state.routes.length === 0) {
      return [route];
    }
    const index = state.index ?? 0;
    if (index < 0 || index >= state.routes.length) {
      return [route];
    }
    const childActiveRoute = state.routes[index] as RouteWithOptionalState;
    if (!childActiveRoute) {
      return [route];
    }
    return [route, ...getRoutes(childActiveRoute)];
  };

  const rootIndex = rootState.index ?? 0;
  if (rootIndex < 0 || rootIndex >= rootState.routes.length) {
    return [];
  }
  const rootRoute = rootState.routes[rootIndex] as RouteWithOptionalState;
  if (!rootRoute) {
    return [];
  }
  return getRoutes(rootRoute);
};

const checkRoutesHasScreen = (routes: RouteWithOptionalState[], screen: Screens | [Screens, object]) => {
  return routes.some((route) => {
    if (
      route.name &&
      (route.name === screen || route.name === screen?.[0] || route.name === (screen?.[1] as any)?.screen)
    ) {
      return true;
    }
  });
};

export const DrawerMenu = () => {
  const dispatch = useAppDispatch();
  const [isInitRender, setIsInitRender] = React.useState(true);
  const token = useSelector(profileTokenSelector());
  const theme = useTheme();
  const styles = useStyles(theme, isInitRender);
  const holderProfile = useSelector(holderProfileSelector());
  const userFullName = holderProfile?.displayName + `${Settings.getIsTest() ? ' (Тестовый)' : ''}`;

  React.useEffect(() => {
    if (token && !holderProfile) {
      initialLoading(dispatch);
    }
  }, [token]);

  const drawerPosition = 'right';

  React.useEffect(() => {
    setTimeout(() => {
      setIsInitRender(false);
    });
  }, [isInitRender]);

  return (
    <Drawer.Navigator
      initialRouteName={Screens.MainTabs}
      drawerContent={(props) => <DrawerContent {...props} userFullName={userFullName} isInitRender={isInitRender} />}
      screenOptions={{
        headerShown: false,
        swipeEdgeWidth: 0,
        drawerStyle: styles.container,
        drawerPosition: drawerPosition,
        drawerType: 'front',
      }}>
      <Drawer.Screen name={Screens.MainTabs} component={BottomStackNavigator} />
    </Drawer.Navigator>
  );
};

interface IDrawerContentProps extends DrawerContentComponentProps {
  userFullName: string;
  isInitRender: boolean;
}

const DrawerContent = (props: IDrawerContentProps) => {
  const theme = useTheme();
  const {userFullName, navigation, isInitRender} = props;
  const styles = useStyles(theme, isInitRender);
  const activeRoutes = getActiveRoutes(props.state);
  const themeSettings = useSelector(themeSelector());
  const dispatch = useAppDispatch();
  const locality = useLocality();

  const Avatar = () => <HolderAvatar preview size={36} style={[styles.holderAvatar]} />;
  const UserName = () => (
    <Text testID='userFullName-drawer-id' style={styles.userFullNameText}>
      {userFullName}
    </Text>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <TouchableOpacity
        testID='routing-to-profile-id'
        style={[styles.profileContainer]}
        onPress={() => {
          openTabRoot(navigation, Screens.Profile);
          navigation.dispatch(DrawerActions.closeDrawer());
        }}>
        <Avatar />
        <UserName />
        {/* <Chevron /> */}
      </TouchableOpacity>
      <DrawerContentScrollView {...props} contentContainerStyle={styles.drawerContentContainer}>
        {mobileMenuItems.map((menuItem, itemIndex) => {
          const routesHasScreen = !!menuItem.screen && checkRoutesHasScreen(activeRoutes, menuItem.screen);
          const focused =
            routesHasScreen ||
            // Данная провека необходима чтобы подсветить пунт меню "Главная" при старте приложения,
            // до тех пор пока не появится более универсальное решение.
            (menuItem.screen === Screens.Main &&
              activeRoutes.length === 1 &&
              activeRoutes[0].name === Screens.MainTabs);

          return (
            <TouchableOpacity
              testID={`mobile-menu-item-${itemIndex}-${
                typeof menuItem.name === 'string' ? menuItem.name : 'complexName'
              }-id`}
              key={itemIndex}
              onPress={() => {
                const targetScreen = Array.isArray(menuItem.screen) ? menuItem.screen[0] : menuItem.screen;
                openTabRoot(navigation, targetScreen);
                navigation.dispatch(DrawerActions.closeDrawer());
              }}>
              <View
                style={[
                  styles.drawerItem,
                  mobileMenuItems.length - 1 === itemIndex ? styles.drawerItemBoldBorder : {},
                ]}>
                {typeof menuItem.name === 'string' ? (
                  <Text
                    style={[
                      styles.drawerItemLabel,
                      focused ? styles.activeDrawerItemLabel : null,
                      menuItem.thin ? styles.drawerItemLabelThin : {},
                    ]}>
                    {menuItem.name}
                  </Text>
                ) : (
                  <View style={{flexGrow: 0, width: 'auto', alignSelf: 'flex-start'}}>
                    {menuItem.name([styles.drawerItemLabel, focused ? styles.activeDrawerItemLabel : null])}
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
        <View style={styles.bottomContainer}>
          <View style={styles.uscLogoContainer}>
            <LogoBlock />
          </View>
        </View>
        <View style={styles.spacer} />
        {locality ? (
          <TouchableOpacity
            testID='change-city-mobile'
            onPress={() => {
              (navigation.navigate as any)(Screens.MainTabs, {
                screen: Screens.Main,
                params: {screen: Screens.ChangeCityMobile},
              });
            }}>
            <View style={[styles.drawerItem, styles.drawerItemLower, styles.drawerItemNoBorder]}>
              <IconBlock color={theme.color.labelText} iconComponent={images.MapPoint} strokeWidth={0.8} />
              <Text
                numberOfLines={1}
                style={[
                  styles.drawerItemLabel,
                  styles.drawerItemLabelThin,
                  styles.drawerItemLabelGrey,
                  checkRoutesHasScreen(activeRoutes, Screens.ChangeCityMobile) ? styles.activeDrawerItemLabel : null,
                ]}>
                {locality?.name}
              </Text>
            </View>
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity
          testID={
            themeSettings.visuallyImpaired === VisImpStatus.Disabled
              ? 'visuallyImpaired-state-btn-id'
              : 'visuallyNormal-setting-btn-id'
          }
          onPress={() => {
            if (themeSettings.visuallyImpaired === VisImpStatus.Active) {
              dispatch(changeThemeProperty('visuallyImpaired', VisImpStatus.Disabled));
              openTabRoot(navigation, Screens.Main);
            } else {
              dispatch(changeThemeProperty('visuallyImpaired', VisImpStatus.Active));
            }
            navigation.dispatch(DrawerActions.closeDrawer());
          }}>
          <View
            style={[
              styles.drawerItem,
              styles.drawerItemLower,
              Platform.OS === 'web' && themeSettings.visuallyImpaired === VisImpStatus.Disabled
                ? styles.drawerItemNoBorder
                : null,
            ]}>
            <IconBlock
              color={theme.color.labelText}
              iconComponent={themeSettings.visuallyImpaired === VisImpStatus.Disabled ? images.Eye : images.EyeOff}
              strokeWidth={themeSettings.visuallyImpaired === VisImpStatus.Disabled ? 1.2 : 0.3}
            />
            <Text
              numberOfLines={1}
              style={[
                styles.drawerItemLabel,
                styles.drawerItemLabelThin,
                styles.drawerItemLabelGrey,
                checkRoutesHasScreen(activeRoutes, Screens.VisuallyImpairedSettings)
                  ? styles.activeDrawerItemLabel
                  : null,
              ]}>
              {themeSettings.visuallyImpaired === VisImpStatus.Disabled ? 'Версия для слабовидящих' : 'Обычная версия'}
            </Text>
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          testID='visuallyImpaired-state-btn-id'
          style={themeSettings.visuallyImpaired === VisImpStatus.Disabled ? styles.hideDrawerItem : null}
          onPress={() => {
            openTabRoot(navigation, Screens.VisuallyImpairedSettings);
            navigation.dispatch(DrawerActions.closeDrawer());
          }}>
          <View
            style={[
              styles.drawerItem,
              styles.drawerItemLower,
              Platform.OS === 'web' ? styles.drawerItemNoBorder : null,
            ]}>
            <IconBlock color={theme.color.labelText} iconComponent={images.Eye} strokeWidth={1.2} />
            <Text
              numberOfLines={1}
              style={[styles.drawerItemLabel, styles.drawerItemLabelThin, styles.drawerItemLabelGrey]}>
              Настройки для слабовидящих
            </Text>
          </View>
        </TouchableOpacity>
        {Platform.OS === 'web' ? null : (
          <TouchableOpacity
            testID='about-app-btn-id'
            onPress={() => {
              openTabRoot(navigation, Screens.AboutApplication);
              navigation.dispatch(DrawerActions.closeDrawer());
            }}>
            <View style={[styles.drawerItem, styles.drawerItemLower, styles.drawerItemNoBorder]}>
              <Text
                style={[
                  styles.drawerItemLabel,
                  styles.drawerItemLabelThin,
                  styles.drawerItemLabelGrey,
                  checkRoutesHasScreen(activeRoutes, Screens.AboutApplication) ? styles.activeDrawerItemLabel : null,
                ]}>
                О приложении
              </Text>
            </View>
          </TouchableOpacity>
        )}
      </DrawerContentScrollView>
    </SafeAreaView>
  );
};

const useStyles = (theme: Theme, isInitRender: boolean) =>
  StyleSheet.create({
    container: {
      width: 310,
      display: isInitRender ? 'none' : 'flex',
    },
    safeArea: {flex: 1, backgroundColor: theme.color.backgroundMenuColor, display: isInitRender ? 'none' : 'flex'},
    profileContainer: {
      ...theme.drawerMenu.profileContainer,
      height: 76,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      shadowColor: theme.color.primaryBlackColor,
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
      paddingRight: 10,
      paddingLeft: contentHorizontalOffset,
    },
    profileContainerReverse: {
      flexDirection: 'row-reverse',
    },
    holderAvatar: {
      marginRight: 10,
    },
    userFullNameText: {
      ...theme.fonts.body,
      flexGrow: 1,
      flexShrink: 1,
    },
    chevronRightIconContainer: {
      width: 36,
      height: 50,
      justifyContent: 'center',
      alignItems: 'center',
    },
    chevronLeftIconContainer: {
      width: 20,
      height: 20,
      marginHorizontal: 8,
      marginRight: 20,
    },
    drawerContentContainer: {
      flexGrow: 1,
      paddingTop: 20,
      paddingBottom: Platform.OS === 'ios' ? 18 : 36,
    },
    drawerItem: {
      ...theme.drawerMenu.drawerItem,
      marginHorizontal: contentHorizontalOffset,
      marginVertical: 0,
      paddingHorizontal: 0,
      borderRadius: 0,
      borderBottomWidth: 1,
      paddingVertical: 12,
      flexDirection: 'row',
      alignItems: 'center',
    },
    drawerItemBoldBorder: theme.drawerMenu.drawerItemBoldBorder,
    drawerItemNoBorder: {borderBottomWidth: 0},
    drawerItemLower: {
      paddingVertical: 6,
    },
    drawerItemLabel: {
      ...theme.fonts.h6Light,
      ...theme.drawerMenu.drawerItemLabel,
      lineHeight: 24,
    },
    drawerItemLabelThin: {
      ...theme.fonts.body,
    },
    drawerItemLabelGrey: {
      color: theme.color.labelText,
    },
    activeDrawerItemLabel: theme.drawerMenu.activeDrawerItemLabel,
    spacer: {
      flexGrow: 1,
    },
    bottomContainer: {
      marginTop: 12,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    logoutButton: {
      height: 24,
      display: 'flex',
      flexDirection: 'row',
      marginTop: 35,
      marginRight: 25,
    },
    logoutButtonLabel: {
      ...theme.drawerMenu.logoutButtonLabel,
      ...theme.fonts.body,
    },
    logoutIconContainer: {
      width: 24,
      height: 24,
      marginRight: 4,
    },
    uscLogoContainer: {
      marginLeft: contentHorizontalOffset,
      marginBottom: 18,
    },
    hideDrawerItem: {
      display: 'none',
    },
  });
